import { useState } from 'react';
import { gql } from '@/lib/gql';
import { SpinnerGap } from '@phosphor-icons/react';
import RecipeCard from '@/components/RecipeCard';

interface MatchedRecipe {
  recipe: {
    id: string;
    slug: string | null;
    title: string;
    cookTime: number | null;
    prepTime: number | null;
    servings: number | null;
    source: string;
    tags: string[];
    photoUrl: string | null;
    queued: boolean;
  };
  matchedCount: number;
  missingCount: number;
  totalCount: number;
  matchPercent: number;
  missingIngredients: string[];
}

interface Props {
  ingredientCount: number;
  kitchenSlug: string;
}

const MATCH_QUERY = `
  query MatchRecipes($kitchenSlug: String, $limit: Int) {
    matchRecipes(kitchenSlug: $kitchenSlug, limit: $limit) {
      matchedCount
      missingCount
      totalCount
      matchPercent
      missingIngredients
      recipe {
        id
        slug
        title
        cookTime
        prepTime
        servings
        source
        tags
        photoUrl
        queued
      }
    }
  }
`;

export default function WhatCanICookButton({ ingredientCount, kitchenSlug }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<MatchedRecipe[] | null>(null);

  async function handleSearch() {
    setLoading(true);
    setError(null);
    try {
      const data = await gql<{ matchRecipes: MatchedRecipe[] }>(MATCH_QUERY, {
        kitchenSlug,
        limit: 12,
      });
      setMatches(data.matchRecipes);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleSearch}
        disabled={loading || ingredientCount === 0}
        aria-busy={loading}
        aria-describedby={error ? 'match-error' : undefined}
        className="btn-primary text-base px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <SpinnerGap size={16} className="animate-spin" aria-hidden />
            Searching…
          </>
        ) : (
          'What can I cook?'
        )}
      </button>

      {ingredientCount === 0 && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
          Add ingredients to your pantry first.{' '}
          <a href="/ingredients#stage" className="underline hover:text-accent">
            Go to Pantry →
          </a>
        </p>
      )}

      {error && (
        <p id="match-error" role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {matches && matches.length === 0 && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
          No recipes matched anything in your pantry yet. Try importing a few from{' '}
          <a href="recipes/import#stage" className="underline hover:text-accent">
            Import Recipes
          </a>
          .
        </p>
      )}

      {matches && matches.length > 0 && (
        <ul role="list" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
          {matches.map((m) => (
            <li key={m.recipe.id} className="grid grid-rows-[subgrid] row-span-4 mb-4">
              <RecipeCard recipe={m.recipe} />
              <p className="px-4 pb-3 text-xs text-[var(--color-text-secondary)]">
                {m.missingCount === 0 ? (
                  <span className="font-semibold text-accent">You have everything!</span>
                ) : (
                  <>
                    {m.matchedCount}/{m.totalCount} on hand — missing{' '}
                    {m.missingIngredients.slice(0, 3).join(', ')}
                    {m.missingIngredients.length > 3 ? ', …' : ''}
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
